/**
 * viewSeenStore — tracks when each view was last visited.
 *
 * Persisted in `db.setting_local` (device-local, never synced).
 * Used for "new task" indicators: sidebar red dots + in-view banners.
 *
 * Key format in DB: `#view-seen:today`, `#view-seen:project:abc123`
 * Key format in store: `today`, `project:abc123`
 */

import { create } from "zustand"
import { db } from "@/db/schema"
import { now } from "@/shared/utils/date/dateUtils"
import type { ViewId } from "@/features/views/viewRegistry"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DB_PREFIX = "#view-seen:"

/** Build a composite key for dynamic views (project:id, list:id, etc.) */
export function viewSeenKey(
  viewId: ViewId,
  containerId?: string | null,
): string {
  return containerId ? `${viewId}:${containerId}` : viewId
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface ViewSeenState {
  /** viewKey → millisecond timestamp string */
  timestamps: Record<string, string>
  /** Whether the store has been hydrated from DB */
  hydrated: boolean

  // Actions
  /** Record current time as "last seen" for a view */
  markSeen: (key: string) => void
  /** Get the last-seen timestamp for a view (undefined = never seen) */
  getLastSeen: (key: string) => string | undefined
  /** Load all view-seen entries from DB */
  hydrate: () => Promise<void>
}

export const useViewSeenStore = create<ViewSeenState>((set, get) => ({
  timestamps: {},
  hydrated: false,

  markSeen: (key) => {
    const timestamp = now()

    // Update store immediately (optimistic)
    set((s) => ({
      timestamps: { ...s.timestamps, [key]: timestamp },
    }))

    // Persist to DB (fire-and-forget)
    db.setting_local
      .put({
        id: `${DB_PREFIX}${key}`,
        value: timestamp,
        device: "local",
        createdAt: timestamp,
        createdBy: "local",
        updatedAt: timestamp,
        updatedBy: "local",
      })
      .catch(console.error)
  },

  getLastSeen: (key) => get().timestamps[key],

  hydrate: async () => {
    if (get().hydrated) return

    try {
      // Fetch all view-seen entries from setting_local in one query
      const entries = await db.setting_local
        .where("id")
        .startsWith(DB_PREFIX)
        .toArray()

      const timestamps: Record<string, string> = {}
      for (const entry of entries) {
        if (!entry.id) continue
        const key = entry.id.slice(DB_PREFIX.length)
        timestamps[key] = entry.value
      }

      set({ timestamps, hydrated: true })
    } catch (err) {
      console.error("Failed to hydrate viewSeenStore:", err)
      set({ hydrated: true }) // Don't block on error
    }
  },
}))
